// Sidebar.js
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useChatUnread } from './chatUnread';
import useIsMobile from './useIsMobile';
import ClickHouseStatus from './ClickHouseStatus';
import { ProviderMark } from './providerLogos';
import { useAppUpdate } from './appUpdate';
import './Sidebar.css';

/* ─── Sign-out icon ─── */
function SignOutIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" width="10" height="10">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

/* ─── The data providers' own marks ───
 *
 * A provider page is named by the provider's mark rather than by a glyph drawn to suggest one: a
 * Catapult chevron or the SkillCorner arrows are recognised at a glance, where "a tracked run" or
 * "stacked database discs" only read once you already know which page you are looking for.
 *
 * The artwork is published out of shapes/data providers/ by scripts/copy_logos.py and reached
 * through the manifest, so a mark resupplied as an .svg keeps working. The glyphs below are what
 * they replaced, kept as the fallback: they render while the manifest is in flight, and stay if a
 * mark is ever missing, so the sidebar can't show a row of gaps.
 */
const providerGlyph = {
  catapult: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="2" />
      <path d="M4.93 19.07a10 10 0 0 1 0-14.14" />
      <path d="M7.76 16.24a6 6 0 0 1 0-8.49" />
      <path d="M16.24 7.76a6 6 0 0 1 0 8.49" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
    </svg>
  ),
  firstbeat: (
    // heart-rate / HRV waveform (Firstbeat = heart-rate variability analytics)
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12h4l2-6 3.5 12 3-9 1.5 3h6" />
    </svg>
  ),
  orreco: (
    // droplet + check — wellness / biomarker monitoring
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2.7c3 3.6 6 6.9 6 10.3a6 6 0 0 1-12 0c0-3.4 3-6.7 6-10.3Z" />
      <path d="m9.5 13 1.8 1.8L15 11" />
    </svg>
  ),
  impect: (
    // stacked database discs — an external data feed landing in our own tables
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="12" cy="5.5" rx="7" ry="3" />
      <path d="M5 5.5v6c0 1.66 3.13 3 7 3s7-1.34 7-3v-6" />
      <path d="M5 11.5v6c0 1.66 3.13 3 7 3s7-1.34 7-3v-6" />
    </svg>
  ),
  skillcorner: (
    // a tracked run — the camera-derived path SkillCorner sells, landing as points
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 18c3-1 4.5-8 8-8s4.5 5 8 5" strokeDasharray="3 2.5" />
      <circle cx="3" cy="18" r="1.6" />
      <circle cx="19" cy="15" r="1.6" />
      <path d="M11 4.2v2.4" />
    </svg>
  ),
  opta: (
    // a mast radiating into a disc — a data gateway feeding our own tables
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="12" cy="18" rx="7" ry="3" />
      <path d="M12 18V9" />
      <circle cx="12" cy="7" r="1.6" />
      <path d="M8.1 3.6a5.5 5.5 0 0 0 0 6.8" />
      <path d="M15.9 3.6a5.5 5.5 0 0 1 0 6.8" />
    </svg>
  ),
  statsbomb: (
    // a ball on a grid — event data, which is what StatsBomb sell: everything located
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 12h18M9 4v16M15 4v16" strokeDasharray="2 2.5" opacity="0.65" />
      <circle cx="12" cy="12" r="2.6" />
    </svg>
  ),
};

/* A Raw page reads the same provider's feed at full sample rate, so it carries that provider's
 * mark with the trace badged onto the corner — the same relationship the pages have. The badge
 * draws in currentColor, which is also what keeps an active Raw page's icon reacting to the gold
 * the flat mark can't. */
function TraceBadge() {
  return (
    <span className="page-mark-badge">
      <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.7"
        strokeLinecap="round" strokeLinejoin="round">
        <path d="M1.4 7.3c.85 0 .95-3 1.8-3s.95 4.1 1.8 4.1.95-4.5 1.8-4.5.95 3 1.8 3" />
      </svg>
    </span>
  );
}

/* The Ghosted Tracking Creator is StatsBomb's own data with players inferred between sightings, so it
 * wears StatsBomb's mark with the ghost on its top right corner. */
function GhostBadge() {
  return (
    <span className="page-mark-badge page-mark-badge-ghost">
      <svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round">
        <path d="M12 3a7 7 0 0 0-7 7v10.5l2.3-1.7 2.4 1.7 2.3-1.7 2.3 1.7 2.4-1.7 2.3 1.7V10a7 7 0 0 0-7-7Z" />
        <circle cx="9.5" cy="10.5" r="1.6" fill="#fff" stroke="none" />
        <circle cx="14.5" cy="10.5" r="1.6" fill="#fff" stroke="none" />
      </svg>
    </span>
  );
}

/* One provider mark, sized by whichever icon slot it lands in (sidebar, home card, placeholder). */
function Mark({ source, badge = false, ghost = false }) {
  return (
    <span className="page-mark">
      <ProviderMark source={source} className="page-mark-img" fallback={providerGlyph[source]} />
      {badge && <TraceBadge />}
      {ghost && <GhostBadge />}
    </span>
  );
}

/* ─── Thin-stroke SVG icons (24x24 viewBox, strokeWidth 1.5) ─── */
export const pageIcons = {
  catapult: <Mark source="catapult" />,
  catapultRaw: <Mark source="catapult" badge />,
  firstbeat: <Mark source="firstbeat" />,
  firstbeatRaw: <Mark source="firstbeat" badge />,
  orreco: <Mark source="orreco" />,
  impect: <Mark source="impect" />,
  skillcorner: <Mark source="skillcorner" />,
  statsbomb: <Mark source="statsbomb" />,
  statsbombGhost: <Mark source="statsbomb" ghost />,
  /* The two Data Pull pages: what is being pulled, dropping into a tray. */
  dataPullPerformance: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 15.5 6.6 10.2a3.4 3.4 0 0 1 4.8-4.8l.6.6.6-.6a3.4 3.4 0 0 1 4.8 4.8Z" />
      <path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
    </svg>
  ),
  dataPullFootball: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="9" r="6" />
      <path d="m12 6.4 2.5 1.8-1 2.9h-3l-1-2.9Z" />
      <path d="M12 6.4V3M14.5 8.2l3.1-1.2M13.5 11.1l2 2.6M10.5 11.1l-2 2.6M9.5 8.2 6.4 7" />
      <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    </svg>
  ),
  opta: <Mark source="opta" />,

  home: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  chart: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="12" width="4" height="9" rx="1" />
      <rect x="10" y="7" width="4" height="14" rx="1" />
      <rect x="17" y="3" width="4" height="18" rx="1" />
    </svg>
  ),
  gps: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 21s-7-6.5-7-11a7 7 0 0 1 14 0c0 4.5-7 11-7 11Z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  ),
  match: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
  metric: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <line x1="8" y1="6" x2="16" y2="6" />
      <line x1="8" y1="10" x2="10" y2="10" />
      <line x1="12" y1="10" x2="14" y2="10" />
      <line x1="16" y1="10" x2="16" y2="10" />
      <line x1="8" y1="14" x2="10" y2="14" />
      <line x1="12" y1="14" x2="14" y2="14" />
      <line x1="16" y1="14" x2="16" y2="14" />
      <line x1="8" y1="18" x2="10" y2="18" />
      <line x1="12" y1="18" x2="14" y2="18" />
      <line x1="16" y1="18" x2="16" y2="18" />
    </svg>
  ),
  user: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  overview: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  calculator: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <line x1="8" y1="6" x2="16" y2="6" />
      <line x1="16" y1="14" x2="16" y2="18" />
      <path d="M16 10h.01" /><path d="M12 10h.01" /><path d="M8 10h.01" />
      <path d="M12 14h.01" /><path d="M8 14h.01" />
      <path d="M12 18h.01" /><path d="M8 18h.01" />
    </svg>
  ),
  gauge: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="m12 14 4-4" />
      <path d="M3.34 19a10 10 0 1 1 17.32 0" />
    </svg>
  ),
  target: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  ),
  heart: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z" />
      <path d="M3.5 12h4l2-4 3 7 2-4h6" />
    </svg>
  ),
  pulse: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12h4l2-7 4 14 3-9 2 4h5" />
    </svg>
  ),
  /* Wellness Longitudinal Tracking. Deliberately NOT the plain `heart` the daily Wellness
     Monitoring Report uses — the two sit next to each other in the Performance list, and a sidebar
     you navigate by shape needs them to be two shapes. The bars say "over time". */
  heartTrend: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12.6 3.4a3.4 3.4 0 0 0-4.8 0l-.6.6-.6-.6a3.4 3.4 0 0 0-4.8 4.8l.6.6L7.2 13l4.8-4.2.6-.6a3.4 3.4 0 0 0 0-4.8Z" />
      <path d="M14 21v-5" />
      <path d="M18 21v-9" />
      <path d="M22 21v-13" />
    </svg>
  ),
  /* Wellness Lateness. A clock, because the page is about one thing — the time of day a
     questionnaire was filled in — and the hand pointing past the hour is the lateness. */
  clock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5.4l3.4 2" />
    </svg>
  ),
  /* Bodyweights & Hydration. A bathroom scale — the dial and its needle are the whole idea, and at
     13px anything more (a droplet for the hydration half) is a smudge. */
  scale: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="4" />
      <path d="M7.5 10a5 5 0 0 1 9 0" />
      <path d="M12 10.5l1.6-2.6" />
    </svg>
  ),
  /* Travel. A plane on its way — the away trip, which is what the page plans for. */
  travel: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
    </svg>
  ),
  /* Match Minutes. Bars under a clock hand: time on the pitch, added up player by player. */
  dayToDay: (
    // a page off the calendar with the day's pulse across it
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
      <path d="M8 3v4M16 3v4M3.5 9.5h17" />
      <path d="M6.5 15h2.6l1.4-2.6 2.4 4.6 1.5-2h3" />
    </svg>
  ),
  matchMinutes: (    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="9" r="6" />
      <path d="M12 5.8V9l2.2 1.4" />
      <path d="M5 21v-2.5M9.7 21v-3.5M14.3 21v-2M19 21v-4" />
    </svg>
  ),
  /* Availability. A shirt with a tick — the players there to pick from on matchday. */
  availability: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8.5 3.5 4 6l1.6 4 2-.8V20.5h8.8V9.2l2 .8L20 6l-4.5-2.5c-.6 1.4-2 2.3-3.5 2.3s-2.9-.9-3.5-2.3z" />
      <path d="m9.6 13.4 1.7 1.7 3.2-3.4" />
    </svg>
  ),
  /* Rehab. An arrow climbing back up a set of steps, which is the sheet's
     whole story — load built back towards a match. */
  rehab: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 20h4v-4h4v-4h4V8h4" />
      <path d="m16 4 3 4-4 1" />
    </svg>
  ),
  /* Hydration. A droplet with its level line — the squad's urine test, read as one picture. */
  droplet: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3c3.2 4 6 7.3 6 10.6A6 6 0 0 1 6 13.6C6 10.3 8.8 7 12 3z" />
      <path d="M8.6 14.6h6.8" />
    </svg>
  ),
  /* Testing. A caliper: the two arms closing on a fold, which is the measurement the page is
     built around. Drawn as the pinch rather than the whole instrument — at 13px the jaws are the
     only part that reads, and they are also the part that says "this is a measurement of a body"
     rather than of a distance. */
  /* Match Physicals Over Time. Points scattered across a shaded band with a dashed average —
     the season of matches the page draws, each one in or out of its band. */
  matchPhysicals: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="9" width="18" height="6" rx="1" opacity="0.45" />
      <path d="M3 12h18" strokeDasharray="2 2" />
      <circle cx="6" cy="10.5" r="1.4" /><circle cx="10" cy="6" r="1.4" /><circle cx="14" cy="13" r="1.4" /><circle cx="18.5" cy="18" r="1.4" />
    </svg>
  ),
  /* Match Physicals. Bars in two halves split by a dashed half-time line, a line crossing them —
     one player's blocks against his own average. */
  matchBlocks: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 20V13M7.5 20V10M11 20V12M13 20V11M16.5 20V9M20 20V12" opacity="0.55" />
      <path d="M12 4v16" strokeDasharray="1.6 2" />
      <path d="M3 9.5l4.5-3 3.5 2.5M13 8l3.5-2.5L21 8" />
    </svg>
  ),
  conditioning: (
    // a heart-rate trace climbing through the zones toward a peak marker — work held near the top
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 5.5h18" strokeDasharray="2 2.5" opacity="0.6" />
      <path d="M3 19c2.5 0 3-5 5.5-5s2.5 3 4.5 3 2.5-9 4.5-9 2 3 3.5 3" />
    </svg>
  ),
  /* Congestion Management. A fixture calendar with two games in one week — the dots sit close
     together, which is the congestion. */
  congestion: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="16" rx="2.5" />
      <path d="M3 10h18M8 3v4M16 3v4" />
      <circle cx="9" cy="15.5" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="13.5" cy="15.5" r="1.6" fill="currentColor" stroke="none" />
    </svg>
  ),
  /* Nutrition. An apple with its leaf — the body-composition page, drawn as food rather than as
     another scale so it does not read as Bodyweights twice. */
  nutrition: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 7.5c-1.6-1.2-4.6-1.4-6.2.6-2 2.5-.9 7.4 1.6 10.4 1.3 1.6 2.6 2.2 4.6 1.2 2 1 3.3.4 4.6-1.2 2.5-3 3.6-7.9 1.6-10.4-1.6-2-4.6-1.8-6.2-.6z" />
      <path d="M12 7.5c0-1.8.6-3.3 2-4.5" />
      <path d="M12.8 5.2c1.4-.3 2.8.1 3.7 1.1-1.4.5-2.7.2-3.7-1.1z" />
    </svg>
  ),
  testing: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 3v7.5a3 3 0 0 0 6 0V3" />
      <path d="M6.5 13.5h11" />
      <path d="M12 16v5" />
    </svg>
  ),
  board: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 12h18" />
      <path d="M12 3v18" />
    </svg>
  ),
  live: (
    // broadcast — a live capture being read as it is coded
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
      <circle cx="12" cy="12" r="2" />
      <path d="M16.24 7.76a6 6 0 0 1 0 8.49M7.76 16.24a6 6 0 0 1 0-8.49" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 19.07a10 10 0 0 1 0-14.14" />
    </svg>
  ),
  fingerprint: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4" />
      <path d="M14 13.12c0 2.38 0 6.38-1 8.88" />
      <path d="M17.29 21.02c.12-.6.43-2.3.5-3.02" />
      <path d="M2 12a10 10 0 0 1 18-6" />
      <path d="M2 16h.01" />
      <path d="M21.8 16c.2-2 .131-5.354 0-6" />
      <path d="M5 19.5C5.5 18 6 15 6 12a6 6 0 0 1 .34-2" />
      <path d="M8.65 22c.21-.66.45-1.32.57-2" />
      <path d="M9 6.8a6 6 0 0 1 9 5.2v2" />
    </svg>
  ),
  videoUpload: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="13" height="10" rx="2" />
      <path d="m22 8-5 4 5 4V8z" />
      <path d="M8.5 14V10" />
      <path d="m6.75 11.5 1.75-1.75 1.75 1.75" />
    </svg>
  ),
  /* Storage Testing. Two stacked drives — the bucket store — with a play notch: video pulled from it. */
  storage: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="11" cy="5.5" rx="7" ry="2.5" />
      <path d="M4 5.5v6c0 1.4 3.1 2.5 7 2.5" />
      <path d="M18 5.5V10" />
      <path d="M4 11.5v6c0 1.4 3.1 2.5 7 2.5" />
      <path d="m15 14 6 3.5-6 3.5v-7z" />
    </svg>
  ),
  /* Schedule. A week planner: the sheet's grid, with one day's block filled in. */
  schedule: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4.5" width="18" height="16" rx="2" />
      <path d="M8 2.5v4" />
      <path d="M16 2.5v4" />
      <path d="M3 9.5h18" />
      <path d="M9 9.5v11" />
      <path d="M15 9.5v11" />
      <path d="M3 15h18" />
      <rect x="9.6" y="10.1" width="4.8" height="4.3" fill="currentColor" stroke="none" opacity="0.45" />
    </svg>
  ),
  /* Chat. Two speech bubbles, one answering the other — people talking, not a single notice. */
  chat: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 9.5a5.5 5.5 0 0 1-8.2 4.8L3 15l.8-2.6A5.5 5.5 0 1 1 14 9.5z" />
      <path d="M10.2 17.3a5.5 5.5 0 0 0 7.9 2.2L21 20.5l-.9-2.8a5.5 5.5 0 0 0-4.3-8.1" />
    </svg>
  ),
  lineUp: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <circle cx="7" cy="9.5" r="1.6" />
      <circle cx="12" cy="9.5" r="1.6" />
      <circle cx="17" cy="9.5" r="1.6" />
      <path d="M5 15.5h4" />
      <path d="M10 15.5h4" />
      <path d="M15 15.5h4" />
    </svg>
  ),
  videoReduce: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M8 9l3 3m0 0V9.5M11 12H8.5" />
      <path d="M16 15l-3-3m0 0v2.5m0-2.5h2.5" />
    </svg>
  ),
  scoreboard: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="6" width="20" height="12" rx="2" />
      <path d="M12 8v8" />
      <path d="M6 10.5h2.5" />
      <path d="M7.25 10.5v3" />
      <path d="M15.5 10.5H18" />
      <path d="M16.75 10.5v3" />
    </svg>
  ),
  gear: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  fileCode: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
      <polyline points="14 2 14 8 20 8" />
      <path d="m10 13-2 2 2 2" />
      <path d="m14 13 2 2-2 2" />
    </svg>
  ),
  ballInPlay: (
    // stopwatch — ball-in-play time
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="13" r="8" />
      <path d="M12 9v4l2.5 2" />
      <path d="M9 2h6" /><path d="M12 2v2" />
    </svg>
  ),
  metricMaker: (
    // sliders / equation builder — building a custom metric
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" />
      <line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" />
      <line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" />
      <line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" />
    </svg>
  ),
  metricExplorer: (
    // ranked columns under a magnifier — searching a league table of metrics
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="21" x2="4" y2="11" /><line x1="9" y1="21" x2="9" y2="4" />
      <line x1="14" y1="21" x2="14" y2="14" />
      <circle cx="17.5" cy="7.5" r="3.5" /><line x1="20.2" y1="10.2" x2="22.5" y2="12.5" />
    </svg>
  ),
  gameModelSimilarity: (
    // a 3-4-3 on a half pitch beside a bar — a shape, and how close another team comes to it
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2.5" y="3" width="12" height="18" rx="1.5" opacity="0.55" />
      <circle cx="5.2" cy="16.5" r="1" fill="currentColor" /><circle cx="8.5" cy="16.5" r="1" fill="currentColor" /><circle cx="11.8" cy="16.5" r="1" fill="currentColor" />
      <circle cx="5.2" cy="12" r="1" fill="currentColor" /><circle cx="11.8" cy="12" r="1" fill="currentColor" />
      <circle cx="5.2" cy="7.2" r="1" fill="currentColor" /><circle cx="8.5" cy="6.4" r="1" fill="currentColor" /><circle cx="11.8" cy="7.2" r="1" fill="currentColor" />
      <path d="M18 21V11" /><path d="M21.5 21V6" />
    </svg>
  ),
  overTimeMetricExplorer: (
    // one metric's season as a line of matches over a shaded band, with the trend riding through it
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="10" width="18" height="6" rx="1" opacity="0.45" />
      <path d="M3 21h18" opacity="0.6" />
      <path d="M4 15l4-6 4 4 4-7 4 5" />
      <circle cx="8" cy="9" r="1.3" fill="currentColor" /><circle cx="16" cy="6" r="1.3" fill="currentColor" />
    </svg>
  ),
  playerMetricExplorer: (
    // a ranked column beside a single figure — one player read against the rest of his position
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7" cy="6.5" r="2.75" />
      <path d="M2.5 20v-1.5A4.5 4.5 0 0 1 7 14a4.5 4.5 0 0 1 4.5 4.5V20" />
      <line x1="15" y1="20" x2="15" y2="12" /><line x1="19" y1="20" x2="19" y2="6" />
      <line x1="22.5" y1="20" x2="22.5" y2="16" />
    </svg>
  ),
  squadValuations: (
    // a stack of coins beside a euro sign — what the squad is worth
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="8" cy="6" rx="5" ry="2" />
      <path d="M3 6v4c0 1.1 2.24 2 5 2s5-.9 5-2V6" />
      <path d="M3 10v4c0 1.1 2.24 2 5 2s5-.9 5-2v-4" />
      <path d="M3 14v4c0 1.1 2.24 2 5 2s5-.9 5-2v-4" />
      <path d="M21.5 9.5a3.5 3.5 0 1 0 0 5" />
      <line x1="15.5" y1="11.2" x2="20" y2="11.2" /><line x1="15.5" y1="12.8" x2="20" y2="12.8" />
    </svg>
  ),
  squadRating: (
    // a rating line climbing across four season bands — one club's strength over the seasons
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="8" y1="4" x2="8" y2="20" opacity="0.45" /><line x1="13" y1="4" x2="13" y2="20" opacity="0.45" />
      <line x1="18" y1="4" x2="18" y2="20" opacity="0.45" />
      <path d="M3 17l4-3 3 1.5 4-5 3 1 4-5" />
      <circle cx="21" cy="6.5" r="1.2" fill="currentColor" />
    </svg>
  ),
  managerMetricExplorer: (
    // a figure on the touchline beside two columns, one stepping UP from the other — the page is
    // not the metric, it is the change in it when he arrived
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="4.6" r="2.3" />
      <path d="M2.8 20v-6.2A3.2 3.2 0 0 1 6 10.6a3.2 3.2 0 0 1 3.2 3.2V20" />
      <line x1="4.4" y1="14.6" x2="7.6" y2="14.6" />
      <line x1="13.5" y1="20" x2="13.5" y2="15" />
      <line x1="17.5" y1="20" x2="17.5" y2="9" />
      <path d="M20 12.5 22.4 10 22.4 13" />
      <line x1="22.4" y1="10" x2="18.6" y2="7.6" />
    </svg>
  ),
  physicalMetricExplorer: (
    // a ranked column beside a running figure — one player's physical output read against the
    // rest of the squad
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8.5" cy="4.2" r="2.1" />
      <path d="M10.4 8.1 6.8 9.6 5.6 13" />
      <path d="M10.4 8.1 12.2 11.2l-2.6 2.1L11 20" />
      <path d="M9.6 13.3 6 16.4 4 20" />
      <line x1="15.5" y1="20" x2="15.5" y2="13" />
      <line x1="19" y1="20" x2="19" y2="8" />
      <line x1="22.5" y1="20" x2="22.5" y2="16" />
    </svg>
  ),
  attackingP3: (
    // the attacking third with three routes converging on the box — a cross, a run and a carry
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="18" rx="2" />
      <path d="M17 7v10" strokeDasharray="1.6 1.6" />
      <path d="M22 8.5h-3v7h3" />
      <path d="M4 6.5C9 6.5 11.5 9 13.5 11.6" />
      <path d="M4 17.5c5 0 7.5-2.5 9.5-5.1" />
      <path d="M12.4 11.9 15 12l-.6-2.6" />
    </svg>
  ),
  defendingP3: (
    // the same third with the routes turned around — converging on the goal we are defending
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="18" rx="2" />
      <path d="M7 7v10" strokeDasharray="1.6 1.6" />
      <path d="M2 8.5h3v7H2" />
      <path d="M20 6.5C15 6.5 12.5 9 10.5 11.6" />
      <path d="M20 17.5c-5 0-7.5-2.5-9.5-5.1" />
      <path d="m11.6 11.9-2.6.1.6-2.6" />
    </svg>
  ),
  goalsAnalysis: (
    // a ball crossing the line of a goal — the net's uprights, the bar, and the shot going in
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 17V9h18v8" />
      <path d="M3 17h18" />
      <path d="M7 9v8M12 9v8M17 9v8" strokeOpacity="0.45" />
      <path d="M3 13h18" strokeOpacity="0.45" />
      <circle cx="12" cy="13" r="2.3" />
      <path d="M12 22.2c0-2.4 0-4 0-5.6" strokeDasharray="1.6 1.8" />
    </svg>
  ),
  runTypes: (
    // a run cutting through a dashed last line between two defenders — an off-ball run in behind
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.5 10.5h19" strokeDasharray="2 2.2" />
      <circle cx="6.5" cy="10.5" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="17.5" cy="10.5" r="1.4" fill="currentColor" stroke="none" />
      <path d="M9 21c1-5.5 3-11.5 3.6-16.7" />
      <path d="M10 6.2l2.6-2.6 2.2 2.9" />
    </svg>
  ),
  secondBalls: (
    // a long ball arcing in, two heads meeting under it, and the loose ball dropping away
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.5 19C6 8 11 5.5 12 9" strokeDasharray="1.8 1.9" strokeOpacity="0.6" />
      <circle cx="10.6" cy="12.6" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="13.6" cy="12.6" r="1.6" />
      <path d="M12.1 10.4l3.6 3.9 2.6 4.2" />
      <circle cx="19.2" cy="19.6" r="1.6" />
    </svg>
  ),
  scouting: (
    // a scouting card: a head and shoulders beside three lines of a profile, one ticked
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2.5" y="3.5" width="19" height="17" rx="2.5" />
      <circle cx="8" cy="9.5" r="2.4" />
      <path d="M4.6 17a3.4 3.4 0 0 1 6.8 0" />
      <path d="M14 8.5h4.5" /><path d="M14 12h4.5" strokeOpacity="0.6" /><path d="M14 15.5h3" strokeOpacity="0.6" />
    </svg>
  ),
  finalThird: (
    // the final-third line, the box, and two runs arrowing up into it
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.5 9.5h19" strokeDasharray="2 2.2" strokeOpacity="0.55" />
      <path d="M7 2.5v4h10v-4" strokeOpacity="0.55" />
      <path d="M6 21l3.5-11.5" />
      <path d="M7.4 11.6l2.1-2.1 1.3 2.6" />
      <circle cx="6" cy="21" r="1" fill="currentColor" stroke="none" />
      <path d="M17.5 21l-2.5-15" />
      <path d="M13.6 7.6l1.4-1.6 1.6 1.4" />
      <circle cx="17.5" cy="21" r="1" fill="currentColor" stroke="none" />
    </svg>
  ),
  exploitingSpace: (
    // a back line of three dots, a gap between them and a free man arriving into it
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.5 8h19" strokeDasharray="2 2.2" strokeOpacity="0.55" />
      <circle cx="4.5" cy="8" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="19.5" cy="8" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="14.5" r="4.2" strokeDasharray="1.8 1.8" />
      <circle cx="12" cy="14.5" r="1.6" fill="currentColor" stroke="none" />
      <path d="M5 21.5l5-5" />
    </svg>
  ),
  synergies: (
    // three players joined by passing lines, the chain carrying on up the pitch
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="5.5" cy="18" r="2.3" />
      <circle cx="17.5" cy="15" r="2.3" />
      <circle cx="10" cy="6" r="2.3" />
      <path d="M7.7 17.4l7.5-1.8" />
      <path d="M16 13.1l-4.6-5.3" />
      <path d="M12.2 5.2l6.3-2" strokeDasharray="1.6 1.8" />
    </svg>
  ),
  coachingReport: (
    // a report sheet with a scoreline across its head and a pitch on the page beneath it
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2.5" width="16" height="19" rx="2" />
      <path d="M8 6.5h2.2M13.8 6.5H16" />
      <path d="M11.3 6.5h1.4" strokeWidth="2" />
      <rect x="7" y="10" width="10" height="8.5" rx="0.8" />
      <path d="M7 14.25h10" />
      <circle cx="12" cy="14.25" r="1.5" />
    </svg>
  ),
  packingZones: (
    // a pitch of concentric bands radiating from the own goal — the packing-zone model itself
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="2" width="18" height="20" rx="2" />
      <path d="M8 22a4 4 0 0 1 8 0" />
      <path d="M4.6 22a7.4 7.4 0 0 1 14.8 0" />
      <path d="M3 15.4a11 11 0 0 1 18 0" />
      <line x1="8" y1="2" x2="8" y2="22" /><line x1="16" y1="2" x2="16" y2="22" />
    </svg>
  ),
  download: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  ),
  clipboard: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="M9 12h6" /><path d="M9 16h6" />
    </svg>
  ),
  shield: (
    // shield + key — access control / permissions
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
      <circle cx="12" cy="10" r="2" />
      <path d="M12 12v3" />
    </svg>
  ),
  ghost: (
    // a ghost — tracking inferred between sightings, there but not seen
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3a7 7 0 0 0-7 7v10.5l2.3-1.7 2.4 1.7 2.3-1.7 2.3 1.7 2.4-1.7 2.3 1.7V10a7 7 0 0 0-7-7Z" />
      <circle cx="9.5" cy="10.5" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="14.5" cy="10.5" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  ),
  movement: (
    // a player and the curved run he makes
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="5" cy="19" r="2" />
      <path d="M6.8 17.6C11 16 11 9.5 19 7.5" strokeDasharray="3 2.6" />
      <path d="m15.8 4.8 3.7 2.5-2.4 3.7" />
    </svg>
  ),
  playground: (
    // a swing set — the pages where tracking is played with rather than reported
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 20 7 4h10l4 16" />
      <path d="M10 4v10M14 4v10" />
      <path d="M9 14h6" />
    </svg>
  ),
  bodyPose: (    // a figure mid-stride with its joints marked — 29 keypoints on a running player
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12.5" cy="4" r="1.9" />
      <path d="M12.2 7v5.4" />
      <path d="M12.2 8.6 8.6 10.7 6.6 14" />
      <path d="M12.2 8.9l3.9 1.5 1.5 3.2" />
      <path d="M12.2 12.4 9.2 15.6 9.6 20" />
      <path d="M12.2 12.4l3 2.4.8 4.6" />
      <circle cx="9.2" cy="15.6" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="15.2" cy="14.8" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  ),
  tracking: (
    // radar sweep — live tracking data
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="4.5" />
      <path d="M12 12l6-4" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" />
    </svg>
  ),
  oppPhysical: (
    // a runner inside a stopwatch — an opposition player's physical output, timed
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="13.5" r="8" />
      <path d="M9.5 2h5" /><path d="M12 2v3.5" />
      <circle cx="12.6" cy="9.6" r="1" />
      <path d="M13.2 11.4 11 13l1.6 1.7.5 3" />
      <path d="M11 13 9 14.6" /><path d="M12.6 11.9l2.6.9" />
    </svg>
  ),
  oppositionPreview: (
    // a slide with a pie on it — the pre-match deck
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2.5" y="4" width="19" height="13" rx="1.5" />
      <path d="M12 21v-4M8.5 21h7" />
      <path d="M10 7.2a3.4 3.4 0 1 0 3.4 3.4H10z" />
      <path d="M12 6.4a3 3 0 0 1 3 3h-3z" fill="currentColor" stroke="none" />
    </svg>
  ),
  scout: (
    // binoculars — scouting the opposition
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 4h3l1.4 5.5" />
      <path d="M18 4h-3l-1.4 5.5" />
      <circle cx="6" cy="14.5" r="4" />
      <circle cx="18" cy="14.5" r="4" />
      <path d="M10 14.5h4" />
    </svg>
  ),
  trainingFocus: (
    // training cone beside a stacked bar — a session split into blocks of work
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 4l3.5 12h-7L6 4z" />
      <path d="M2 19h9" />
      <path d="M15 20V9" /><path d="M19 20V5" />
      <path d="M13.5 20h7" />
    </svg>
  ),
  setPieceSummary: (
    // corner arc with a target — competition-wide corner summary
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4v16h16" />
      <path d="M4 9a11 11 0 0 1 11 11" />
      <circle cx="15" cy="9" r="2.5" />
    </svg>
  ),
  stopwatch: (
    // A stopwatch with a PLAY inside it rather than two hands — Training Session Information,
    // whose one irreplaceable control is Start/Stop. Two other pages in the rail already draw a
    // stopwatch (Ball In Play, Opposition Physicals) and both use hands; the triangle is what
    // tells this one apart at 15px, and it happens to be the exact glyph on the page's own button.
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="14" r="7.5" />
      <path d="M10.4 11.8 14.2 14l-3.8 2.2z" fill="currentColor" stroke="none" />
      <path d="M9.5 2h5" /><path d="M12 2v3.5" />
    </svg>
  ),
  matchNotes: (
    // A clipboard with a player on it — Match Notes is a team sheet somebody carries round, and
    // the section already has a plain clipboard (Data Entry) and a stopwatch (Training Session
    // Information). The dot-and-shoulders is what separates this one from the clipboard above it
    // at 15px: this clipboard has a PLAYER on it, not a list of rows.
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="2" width="6" height="3.5" rx="1" />
      <path d="M15 3.75h2.5A1.5 1.5 0 0 1 19 5.25v15A1.5 1.5 0 0 1 17.5 21.75h-11A1.5 1.5 0 0 1 5 20.25v-15A1.5 1.5 0 0 1 6.5 3.75H9" />
      <circle cx="12" cy="11.5" r="2.2" />
      <path d="M8.4 18.6a3.9 3.9 0 0 1 7.2 0" />
    </svg>
  ),
  manualEntry: (
    // A pencil writing a pulse line — Manual Data Entry types in the GPS and heart-rate rows a
    // unit or a belt would have recorded. The trace is what separates it from the plain clipboard
    // and the team sheet above it in the same section.
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.5 17h3l2-4 2.5 6 2-4h2" />
      <path d="M15.5 14.5 20.8 9.2a1.6 1.6 0 0 0-2.3-2.3L13.2 12.2l-.7 3z" />
      <path d="M17.3 7.9l2.3 2.3" />
    </svg>
  ),
  injuryIllness: (
    // A medical cross in a shield-less square with a pulse dot — the medical log. The cross is what
    // tells it apart from the clipboards above it in the Data Entry section at 15px.
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="4" />
      <path d="M10 7.5h4v3.5h3.5v4H14v3.5h-4V15H6.5v-4H10z" />
    </svg>
  ),
  tableau: (
    // A pill on a shelf, and the bars it draws. Every other Admin page is a pipe from one
    // provider, and its icon is that provider's mark; this one is the only page in the section
    // that BUILDS something, so it is drawn as the thing you build — a field dropped on a shelf,
    // and the chart underneath it.
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="11" height="3.4" rx="1.7" fill="currentColor" stroke="none" />
      <path d="M16.5 4.7H21" />
      <path d="M4 21V13" /><path d="M9 21v-6" /><path d="M14 21v-10" /><path d="M19 21V9" />
      <path d="M2.5 21h19" />
    </svg>
  ),
  setPiecePositioning: (
    // corner flag with players placed in the box — set-piece positioning
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 21V5" />
      <path d="M4 5h7l-1.7 2.2L11 9.5H4" />
      <circle cx="15" cy="13" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="19.5" cy="11" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="18" cy="17" r="1.6" fill="currentColor" stroke="none" />
      <path d="M2.5 21h19" />
    </svg>
  ),
  idp: (
    // A player with a rising line beside him — an Individual Development Plan is where one player
    // is going. The arrow is what tells it from the plain player glyph in Performance at 15px.
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="7" r="3" />
      <path d="M2.5 20a5.5 5.5 0 0 1 11 0" />
      <path d="M15 17l3-4 2 2 2.5-4.5" />
      <path d="M19.5 10.5h3v3" />
    </svg>
  ),
  gameModel: (
    // A pitch split into the two halves of the model, with a principle stacked in each — the Game
    // Model is defending and attacking, phase by phase.
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M12 3v18" />
      <path d="M6 8h3" /><path d="M6 12h3" /><path d="M6 16h3" />
      <path d="M15 8h3" /><path d="M15 12h3" /><path d="M15 16h3" />
    </svg>
  ),
  setPieces: (
    // corner flag — set pieces
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 21V4" />
      <path d="M6 4h11l-2.5 3L17 10H6" />
      <path d="M4 21h6" />
    </svg>
  ),
};

/* ─── Section icons (smaller, for section headers) ─── */
const sectionIcons = {
  // Home is a link rather than a collapsible group, but it is presented as one
  // of these rows, so it needs a glyph drawn at the section-icon size.
  Home: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  'Data Entry': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="M9 12h6" /><path d="M9 16h6" />
    </svg>
  ),
  Performance: (
    // sport science & medical — heart-pulse (physiological monitoring)
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
      <path d="M3.22 12H9.5l.5-1 2 4.5 2-7 1.5 3.5h5.27" />
    </svg>
  ),
  Coaching: (
    // The move a coach draws: a player, and the run he is being sent on. This section is the
    // tactical half of the app — set pieces, style of play, opposition analysis, chance creation
    // — so a chalkboard run says more about what is inside it than a whistle did.
    //
    // Three strokes, which is what the whistle cost too: at 13px a fourth is a smudge. The dot is
    // deliberately open rather than filled, so it holds the same visual weight as the ring in the
    // Performance heart and the circle in the Admin cog and the rail reads evenly down its length.
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
      <circle cx="5.5" cy="18.5" r="2" />
      <path d="M7.3 17.4C9 13 12 10.5 17 9" />
      <path d="M13.2 8.2 17 9l-1.4 3.6" />
    </svg>
  ),
  'Squad Planning': (
    // Three shirts on a depth chart: one starter, two behind him.
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
      <circle cx="12" cy="6" r="2.5" />
      <circle cx="6" cy="17" r="2.5" />
      <circle cx="18" cy="17" r="2.5" />
      <path d="M10.7 8.2 7.3 14.8" /><path d="m13.3 8.2 3.4 6.6" />
    </svg>
  ),
  Academy: (
    // A graduation cap.
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
      <path d="M2 9.5 12 5l10 4.5L12 14 2 9.5Z" />
      <path d="M6.5 11.8V16c1.5 1.4 3.3 2 5.5 2s4-.6 5.5-2v-4.2" />
    </svg>
  ),
  'Player Development': (
    // A seedling — the section where a player is grown, rather than measured or scouted.
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
      <path d="M12 21v-9" />
      <path d="M12 12C12 7.5 8.5 5 4 5c0 4.5 3.5 7 8 7z" />
      <path d="M12 14c0-3.5 3-6 7.5-6 0 3.5-3 6-7.5 6z" />
    </svg>
  ),
  Admin: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
};

const PAGE_CONFIG = [
  { id: '/', name: 'Home', section: 'top', icon: 'home' },
  // DATA ENTRY
  { id: '/data-entry', name: 'Data Entry', section: 'Data Entry', icon: 'clipboard' },
  { id: '/training-session-information', name: 'Training Session Information', section: 'Data Entry', icon: 'stopwatch' },
  { id: '/match-notes', name: 'Match Notes', section: 'Data Entry', icon: 'matchNotes' },
  { id: '/manual-data-entry', name: 'Manual Data Entry', section: 'Data Entry', icon: 'manualEntry' },
  { id: '/injury-illness', name: 'Injury & Illness', section: 'Data Entry', icon: 'injuryIllness' },
  // PERFORMANCE
  { id: '/day-to-day-summaries', name: 'Day-to-Day Summaries', section: 'Performance', icon: 'dayToDay', noGrant: true },
  { id: '/wellness-monitoring-report', name: 'Day-to-Day Summaries · Daily Wellness Report', section: 'Performance', icon: 'heart', viewOf: '/day-to-day-summaries' },
  { id: '/daily-training-report', name: 'Day-to-Day Summaries · Daily Training Report', section: 'Performance', icon: 'gps', viewOf: '/day-to-day-summaries' },
  { id: '/post-match-report', name: 'Day-to-Day Summaries · Post Match Report', section: 'Performance', icon: 'match', viewOf: '/day-to-day-summaries' },
  { id: '/training-drill-calculator', name: 'Training Drill Calculator', section: 'Performance', icon: 'calculator' },
  { id: '/individual-player-load-tracking-report', name: 'Individual Player Load Tracking Report', section: 'Performance', icon: 'user' },
  { id: '/load-budget-chronic-load-overview', name: 'Load Budget / Chronic Load Overview', section: 'Performance', icon: 'gauge' },
  { id: '/match-performance', name: 'Match Performance', section: 'Performance', icon: 'matchPhysicals', noGrant: true },
  { id: '/match-physicals-over-time', name: 'Match Performance · Match Physicals Over Time', section: 'Performance', icon: 'matchPhysicals', viewOf: '/match-performance' },
  { id: '/match-physicals', name: 'Match Performance · Within Match Physicals Trendline', section: 'Performance', icon: 'matchBlocks', viewOf: '/match-performance' },
  { id: '/physical-player-metric-explorer', name: 'Match Performance · Physical Player Match Metric Explorer', section: 'Performance', icon: 'physicalMetricExplorer', viewOf: '/match-performance' },
  { id: '/match-minutes', name: 'Match Performance · Match Minutes', section: 'Performance', icon: 'matchMinutes', viewOf: '/match-performance' },
  { id: '/nutrition', name: 'Nutrition', section: 'Performance', icon: 'nutrition', noGrant: true },
  { id: '/bodyweights-hydration', name: 'Nutrition · Bodyweights & Hydration', section: 'Performance', icon: 'scale', viewOf: '/nutrition' },
  { id: '/hydration', name: 'Nutrition · Hydration', section: 'Performance', icon: 'droplet', viewOf: '/nutrition' },
  { id: '/skinfolds-bodyweight', name: 'Nutrition · Skinfolds & Bodyweight', section: 'Performance', icon: 'nutrition', viewOf: '/nutrition' },
  { id: '/rehab', name: 'Rehab', section: 'Performance', icon: 'rehab' },
  { id: '/conditioning', name: 'Conditioning', section: 'Performance', icon: 'conditioning' },
  { id: '/wellness-longitudinal-tracking', name: 'Wellness Longitudinal Tracking', section: 'Performance', icon: 'heartTrend' },
  { id: '/wellness-lateness', name: 'Wellness Lateness', section: 'Performance', icon: 'clock' },
  { id: '/testing', name: 'Testing', section: 'Performance', icon: 'testing' },
  { id: '/congestion-management', name: 'Congestion Management', section: 'Performance', icon: 'congestion' },
  { id: '/availability', name: 'Availability', section: 'Performance', icon: 'availability' },
  { id: '/travel', name: 'Travel', section: 'Performance', icon: 'travel' },
  { id: '/heart-rate', name: 'Heart Rate', section: 'Performance', icon: 'pulse' },
  { id: '/opposition-physicals', name: 'Opposition Physicals', section: 'Performance', icon: 'oppPhysical' },
  // SQUAD PLANNING
  { id: '/squad-depth', name: 'Squad Depth', section: 'Squad Planning', icon: 'match' },
  // COACHING
  { id: '/scouting-recruitment', name: 'Scouting & Recruitment', section: 'Coaching', icon: 'scouting' },
  { id: '/coaching-post-match-report', name: 'Post Match Report', section: 'Coaching', icon: 'coachingReport' },
  // One page in the sidebar, its three views granted one by one — as Metric Explorer, below.
  { id: '/team-analysis', name: 'Team Analysis', section: 'Coaching', icon: 'scout', noGrant: true },
  { id: '/opposition-analysis', name: 'Team Analysis · Style & Outcomes', section: 'Coaching', icon: 'scout', viewOf: '/team-analysis' },
  { id: '/opposition-preview', name: 'Opposition Preview', section: 'Coaching', icon: 'oppositionPreview' },
  { id: '/attacking-chance-creation', name: 'Team Analysis · Attacking - Chance Creation', section: 'Coaching', icon: 'attackingP3', viewOf: '/team-analysis' },
  { id: '/defending-chance-prevention', name: 'Team Analysis · Defending - Chance Prevention', section: 'Coaching', icon: 'defendingP3', viewOf: '/team-analysis' },
  { id: '/set-pieces', name: 'Set Pieces', section: 'Coaching', icon: 'setPieces' },
  { id: '/set-pieces-positioning', name: 'Set Pieces - Positioning', section: 'Coaching', icon: 'setPiecePositioning' },
  { id: '/set-piece-summary', name: 'Set Pieces - Competition Overview', section: 'Coaching', icon: 'setPieceSummary' },
  // One page in the sidebar; its three views are granted one by one (pageRoutes.js PAGE_VIEWS), so
  // the page itself is not a grant (`noGrant`) and the views are not nav items (`viewOf`).
  { id: '/metric-explorer', name: 'Metric Explorer', section: 'Coaching', icon: 'metricExplorer', noGrant: true },
  { id: '/team-metric-explorer', name: 'Metric Explorer · Team', section: 'Coaching', icon: 'metricExplorer', viewOf: '/metric-explorer' },
  { id: '/player-metric-explorer', name: 'Metric Explorer · Player', section: 'Coaching', icon: 'playerMetricExplorer', viewOf: '/metric-explorer' },
  { id: '/manager-metric-explorer', name: 'Metric Explorer · Manager', section: 'Coaching', icon: 'managerMetricExplorer', viewOf: '/metric-explorer' },
  { id: '/over-time-metric-explorer', name: 'Over Time Metric Explorer', section: 'Coaching', icon: 'overTimeMetricExplorer' },
  { id: '/game-model-similarity', name: 'Game Model Similarity', section: 'Coaching', icon: 'gameModelSimilarity' },
  { id: '/tactical-xml', name: 'Tactical XML', section: 'Coaching', icon: 'fileCode' },
  { id: '/metric-maker', name: 'Metric Maker', section: 'Coaching', icon: 'metricMaker' },
  { id: '/playground', name: 'Playground', section: 'Coaching', icon: 'playground', noGrant: true },
  { id: '/tracking-playground', name: 'Playground · Tracking Playground', section: 'Coaching', icon: 'tracking', viewOf: '/playground' },
  { id: '/body-pose', name: 'Playground · Body Pose', section: 'Coaching', icon: 'bodyPose', viewOf: '/playground' },
  { id: '/statsbomb-tracking-testing', name: 'Playground · Statsbomb Ghosted Tracking Data', section: 'Coaching', icon: 'ghost', viewOf: '/playground' },
  { id: '/live', name: 'Live', section: 'Coaching', icon: 'live' },
  { id: '/movement', name: 'Movement', section: 'Coaching', icon: 'movement', noGrant: true },
  { id: '/run-types', name: 'Movement · Run Types', section: 'Coaching', icon: 'runTypes', viewOf: '/movement' },
  { id: '/final-third-movement', name: 'Movement · Final Third Movement', section: 'Coaching', icon: 'finalThird', viewOf: '/movement' },
  { id: '/exploiting-space', name: 'Exploiting Space', section: 'Coaching', icon: 'exploitingSpace' },
  { id: '/second-balls', name: '2nd Balls', section: 'Coaching', icon: 'secondBalls' },
  { id: '/player-synergies', name: 'Player Synergies', section: 'Coaching', icon: 'synergies' },
  { id: '/ball-in-play', name: 'Ball In Play', section: 'Coaching', icon: 'ballInPlay' },
  { id: '/training-focus', name: 'Training Focus', section: 'Coaching', icon: 'trainingFocus' },
  { id: '/squad-rating', name: 'Squad Rating', section: 'Coaching', icon: 'squadRating' },
  { id: '/squad-valuations', name: 'Squad Valuations', section: 'Coaching', icon: 'squadValuations' },
  { id: '/goals-analysis', name: 'Goals Analysis', section: 'Coaching', icon: 'goalsAnalysis' },
  { id: '/packing-zones', name: 'Packing Zones', section: 'Coaching', icon: 'packingZones' },
  { id: '/game-model-review', name: 'Game Model Review', section: 'Coaching', icon: 'fingerprint' },
  { id: '/style-of-play', name: 'Style of Play', section: 'Coaching', icon: 'target' },
  // PLAYER DEVELOPMENT
  { id: '/idp', name: 'IDP', section: 'Player Development', icon: 'idp' },
  { id: '/game-model', name: 'Game Model', section: 'Player Development', icon: 'gameModel' },
  // ADMIN
  { id: '/data-pull-performance', name: 'Data Pull - Performance', section: 'Admin', icon: 'dataPullPerformance', noGrant: true },
  { id: '/catapult', name: 'Data Pull - Performance · Catapult', section: 'Admin', icon: 'catapult', viewOf: '/data-pull-performance' },
  { id: '/catapult-raw', name: 'Data Pull - Performance · Catapult Raw', section: 'Admin', icon: 'catapultRaw', viewOf: '/data-pull-performance' },
  { id: '/firstbeat', name: 'Data Pull - Performance · Firstbeat', section: 'Admin', icon: 'firstbeat', viewOf: '/data-pull-performance' },
  { id: '/firstbeat-raw', name: 'Data Pull - Performance · Firstbeat Raw', section: 'Admin', icon: 'firstbeatRaw', viewOf: '/data-pull-performance' },
  { id: '/orreco', name: 'Data Pull - Performance · Orreco', section: 'Admin', icon: 'orreco', viewOf: '/data-pull-performance' },
  { id: '/data-pull-football', name: 'Data Pull - Football', section: 'Admin', icon: 'dataPullFootball', noGrant: true },
  { id: '/impect', name: 'Data Pull - Football · Impect', section: 'Admin', icon: 'impect', viewOf: '/data-pull-football' },
  { id: '/skillcorner', name: 'Data Pull - Football · SkillCorner', section: 'Admin', icon: 'skillcorner', viewOf: '/data-pull-football' },
  { id: '/statsbomb', name: 'Data Pull - Football · StatsBomb', section: 'Admin', icon: 'statsbomb', viewOf: '/data-pull-football' },
  { id: '/statsbomb-tracking-creator', name: 'Data Pull - Football · Statsbomb Ghosted Tracking Creator', section: 'Admin', icon: 'statsbombGhost', viewOf: '/data-pull-football' },
  { id: '/opta', name: 'Data Pull - Football · Opta', section: 'Admin', icon: 'opta', viewOf: '/data-pull-football' },
  { id: '/tableau', name: 'Tableau', section: 'Admin', icon: 'tableau' },
  { id: '/scoreboard-creator', name: 'Scoreboard Creator', section: 'Admin', icon: 'scoreboard' },
  { id: '/line-up-creator', name: 'Line Up Creator', section: 'Admin', icon: 'lineUp' },
  { id: '/video-size-reducer', name: 'Video Size Reducer', section: 'Admin', icon: 'videoReduce' },
  { id: '/match-video-uploader', name: 'Match Video Uploader', section: 'Admin', icon: 'videoUpload' },
  { id: '/storage-testing', name: 'Storage Testing', section: 'Admin', icon: 'storage' },
  { id: '/schedule', name: 'Schedule', section: 'Admin', icon: 'schedule' },
  { id: '/chat', name: 'Chat', section: 'Admin', icon: 'chat' },
  { id: '/permissions', name: 'Permissions', section: 'Admin', icon: 'shield', adminOnly: true },
];

const SECTIONS = ['Data Entry', 'Performance', 'Squad Planning', 'Coaching', 'Player Development', 'Academy', 'Admin'];
// Shown to everyone before any page exists in them; every other empty section stays hidden.
const EMPTY_SECTIONS_SHOWN = new Set(['Academy']);

// Pages the admin can grant/revoke per user (everything except always-open Home
// and the admin-only Permissions page). Shared with the Permissions grid.
export const ACCESS_PAGES = PAGE_CONFIG.filter(
  (p) => p.section !== 'top' && !p.adminOnly && !p.noGrant
);
// The pages a user navigates to (sidebar, Home): a page's views are reached through the page.
export const NAV_PAGES = PAGE_CONFIG.filter((p) => !p.viewOf);
export { PAGE_CONFIG, SECTIONS, EMPTY_SECTIONS_SHOWN };

/* ─── Accent ring layers for active nav item ─── */
function GoldRing() {
  return (
    <>
      <div className="nav-gold-glow" />
      <div className="nav-gold-clip">
        <div className="nav-gold-gradient" />
      </div>
      <div className="nav-gold-plate" />
    </>
  );
}

/* ─── NavIcon component ─── */
function NavIcon({ name }) {
  return <span className="nav-icon">{pageIcons[name] || pageIcons.home}</span>;
}

/* ─── Glassmorphic Sidebar Tooltip (portal) ─── */
function SidebarTooltip({ text, anchorRef, visible }) {
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!visible || !anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    setPos({ top: r.top + r.height / 2, left: r.right + 12 });
  }, [visible, anchorRef]);

  if (!visible || !text) return null;
  return ReactDOM.createPortal(
    <div className="sidebar-tooltip" style={{ top: pos.top, left: pos.left }}>
      {text}
    </div>,
    document.body
  );
}

function useSidebarTooltip(isCollapsed) {
  const [show, setShow] = useState(false);
  const ref = useRef(null);
  const timer = useRef(null);
  const onEnter = useCallback(() => {
    if (!isCollapsed) return;
    timer.current = setTimeout(() => setShow(true), 300);
  }, [isCollapsed]);
  const onLeave = useCallback(() => { clearTimeout(timer.current); setShow(false); }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  return { ref, show, onEnter, onLeave };
}

/* ─── NavItem with tooltip ─── */
function NavItem({ page, active, isCollapsed, children }) {
  const tt = useSidebarTooltip(isCollapsed);
  return (
    <Link
      ref={tt.ref}
      to={page.id}
      className={`nav-item ${active ? 'active' : ''}`}
      onMouseEnter={tt.onEnter}
      onMouseLeave={tt.onLeave}
    >
      {children}
      <SidebarTooltip text={page.name} anchorRef={tt.ref} visible={tt.show} />
    </Link>
  );
}

/* ─── A top-level page presented as a section row ───
 * Home isn't a collapsible group, but it reads as a peer of Data Entry /
 * Performance / Coaching / Admin rather than as a page inside one — so it uses
 * the same markup as SectionHeader (same height, tile, uppercase title, and the
 * same centring when collapsed) with a Link in place of the toggle, and no
 * chevron since there is nothing to expand. */
function SectionLink({ page, icon, active, isCollapsed }) {
  const tt = useSidebarTooltip(isCollapsed);
  return (
    <Link
      ref={tt.ref}
      to={page.id}
      className={`section-header section-link ${active ? 'has-active' : ''}`}
      onMouseEnter={tt.onEnter}
      onMouseLeave={tt.onLeave}
    >
      <span className="section-header-icon">{icon}</span>
      <span className="section-title">{page.name}</span>
      <SidebarTooltip text={page.name} anchorRef={tt.ref} visible={tt.show} />
    </Link>
  );
}

/* ─── Chevron icon for collapsible sections ─── */
function ChevronIcon({ expanded }) {
  return (
    <svg
      className={`section-chevron ${expanded ? 'expanded' : ''}`}
      viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      width="10" height="10"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

function SectionHeader({ sectionName, icon, hasActive, expanded, onClick, isCollapsed, badge = 0 }) {
  const tt = useSidebarTooltip(isCollapsed);
  return (
    <div
      ref={tt.ref}
      className={`section-header ${hasActive ? 'has-active' : ''}`}
      onClick={onClick}
      onMouseEnter={tt.onEnter}
      onMouseLeave={tt.onLeave}
    >
      <span className="section-header-icon">{icon}</span>
      <span className="section-title">{sectionName}</span>
      {/* A folded section still says something inside it is waiting (Chat's unread count). */}
      {badge > 0 && !expanded && <span className="nav-badge section-badge" title={`${badge} unread`}>{badge > 99 ? '99+' : badge}</span>}
      <ChevronIcon expanded={expanded} />
      <SidebarTooltip text={sectionName} anchorRef={tt.ref} visible={tt.show} />
    </div>
  );
}

function SidebarToggleWithTooltip({ isCollapsed, toggleSidebar, isMobile }) {
  const [show, setShow] = useState(false);
  const ref = useRef(null);
  const timer = useRef(null);
  const onEnter = useCallback(() => {
    timer.current = setTimeout(() => setShow(true), 300);
  }, []);
  const onLeave = useCallback(() => { clearTimeout(timer.current); setShow(false); }, []);
  const onClick = useCallback(() => { clearTimeout(timer.current); setShow(false); toggleSidebar(); }, [toggleSidebar]);
  useEffect(() => () => clearTimeout(timer.current), []);
  const label = isMobile ? 'Close' : (isCollapsed ? 'Expand' : 'Collapse');
  return (
    <button ref={ref} className="sidebar-toggle" onClick={onClick} onMouseEnter={onEnter} onMouseLeave={onLeave}
      aria-label={label}>
      {/* In the drawer the same button closes it, so it wears a cross rather than a chevron that
          would point at a rail width nothing on a phone has. */}
      {isMobile ? (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
          style={{ transform: isCollapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s ease' }}>
          <path d="M15 18l-6-6 6-6" />
        </svg>
      )}
      <SidebarTooltip text={label} anchorRef={ref} visible={show} />
    </button>
  );
}

/* An arrow into a tray: the update waiting to be loaded. Drawn here like the rest of this file's
   icons rather than pulled from lucide, which nothing else in it uses. */
const UpdateIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 16V4M12 4 7 9M12 4l5 5" />
    <path d="M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2" />
  </svg>
);

function Sidebar({ isCollapsed, toggleSidebar }) {
  const location = useLocation();
  const { logout, user, can } = useAuth();
  const appUpdated = useAppUpdate();
  /* ─── The phone drawer ───
   * There is no room for a rail beside the content at 375px, so the sidebar slides in over the
   * page instead and is summoned by a floating button. `isCollapsed` — the desktop rail's
   * expanded/narrow state — is meaningless there: a 64px column of unlabelled icons with no
   * hover tooltips to explain them is a guessing game, so the drawer always renders expanded.
   * Its own open/closed state lives here rather than in main.js because nothing outside the
   * sidebar needs to know about it; the content underneath doesn't move. */
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const collapsed = isMobile ? false : isCollapsed;

  // Tapping a page is the end of the errand — the drawer gets out of the way rather than leaving
  // the reader to dismiss it before they can see what they navigated to.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Leaving phone width with the drawer open would strand a fixed overlay over the desktop layout.
  useEffect(() => { if (!isMobile) setDrawerOpen(false); }, [isMobile]);

  // The page behind a drawer must not scroll under the finger.
  useEffect(() => {
    if (!isMobile || !drawerOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isMobile, drawerOpen]);
  // Nothing is open on arrival. Data Entry and Performance used to be pinned open here, which
  // meant the app always opened on someone else's two sections — and for a user granted neither,
  // on two headings they can't even see the contents of. The effect below opens whichever
  // section holds the page you're actually on, so a deep link still reveals itself.
  const [expandedSections, setExpandedSections] = useState({});

  const isActive = (path) => location.pathname === path;

  // Only the pages this user is allowed to open. Memoised on `can`, which only changes with the
  // signed-in user — the sidebar re-renders on every route change, and re-checking every page's
  // grant each time bought nothing.
  const visiblePages = useMemo(() => NAV_PAGES.filter(p => can(p.id)), [can]);
  const chatUnread = useChatUnread(user && user.username, can('/chat'));
  const pagesBySection = useMemo(() => {
    const m = {};
    for (const p of visiblePages) (m[p.section] = m[p.section] || []).push(p);
    return m;
  }, [visiblePages]);

  // Auto-expand section if a page within it is active
  useEffect(() => {
    for (const sectionName of SECTIONS) {
      const sectionPages = NAV_PAGES.filter(p => p.section === sectionName);
      if (sectionPages.some(p => location.pathname === p.id)) {
        setExpandedSections(prev => ({ ...prev, [sectionName]: true }));
      }
    }
  }, [location.pathname]);

  const toggleSection = (name) => {
    setExpandedSections(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const topPages = pagesBySection.top || [];
  const displayName = (user && user.displayName) || 'User';
  const initials = displayName.split(/\s+/).filter(Boolean).slice(0, 2)
    .map(w => w.charAt(0).toUpperCase()).join('') || 'U';
  const roleLabel = user && user.isAdmin ? 'Admin' : 'User';

  return (
    <>
      {/* Rendered on every viewport and hidden off-phone by mobile.css, so a window resize can
          never catch the drawer half-built — but it stands down while the drawer is out, which
          covers this corner and carries its own close button. */}
      <button
        type="button"
        className="sidebar-mobile-open"
        onClick={() => setDrawerOpen(true)}
        aria-label="Open menu"
        hidden={isMobile && drawerOpen}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      </button>
      {isMobile && drawerOpen && (
        <div className="sidebar-backdrop" onClick={() => setDrawerOpen(false)} />
      )}
      <div className={`sidebar ${collapsed ? 'collapsed' : ''}${isMobile && drawerOpen ? ' mobile-open' : ''}`}>
        {/* Expand/Collapse toggle inside sidebar top */}
        <SidebarToggleWithTooltip
          isCollapsed={collapsed}
          isMobile={isMobile}
          toggleSidebar={isMobile ? () => setDrawerOpen(false) : toggleSidebar}
        />

        <div className="nav-section">
          {/* Top section (Home only) */}
          <div className="section">
            {topPages.map((page) => (
              <SectionLink
                key={page.id}
                page={page}
                icon={sectionIcons[page.name] || sectionIcons.Home}
                active={isActive(page.id)}
                isCollapsed={collapsed}
              />
            ))}
          </div>

          {SECTIONS.map((sectionName) => {
            const sectionPages = pagesBySection[sectionName] || [];
            if (sectionPages.length === 0 && !EMPTY_SECTIONS_SHOWN.has(sectionName)) return null; // hide sections with no accessible pages
            const expanded = !!expandedSections[sectionName];
            const hasActive = sectionPages.some(p => isActive(p.id));
            return (
              <div className={`section ${expanded ? 'section-expanded' : ''}`} key={sectionName}>
                <SectionHeader
                  sectionName={sectionName}
                  icon={sectionIcons[sectionName]}
                  hasActive={hasActive}
                  expanded={expanded}
                  onClick={() => toggleSection(sectionName)}
                  isCollapsed={collapsed}
                  badge={sectionPages.some((p) => p.id === '/chat') ? chatUnread : 0}
                />
                {expanded && (
                  <div className="section-items">
                    {sectionPages.length === 0 && !collapsed && (
                      <div className="section-empty">No items yet</div>
                    )}
                    {sectionPages.map((page) => {
                      const active = isActive(page.id);
                      return (
                        <NavItem key={page.id} page={page} active={active} isCollapsed={collapsed}>
                          {active && <GoldRing />}
                          <NavIcon name={page.icon} />
                          <span className="nav-label">{page.name}</span>
                          {page.id === '/chat' && chatUnread > 0 && (
                            <span className="nav-badge" title={`${chatUnread} unread`}>{chatUnread > 99 ? '99+' : chatUnread}</span>
                          )}
                        </NavItem>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="sidebar-bottom">
          {/* A release has replaced the bundle this tab is running (appUpdate.js). Signing out and
              back in reloads the page, which is what picks the new one up. Collapsed, the arrow
              alone carries it, with the words on its tooltip. */}
          {appUpdated && (
            <button type="button" className="sidebar-update" onClick={logout}
              title="The app has been updated — sign out and back in to load it">
              <UpdateIcon size={isCollapsed ? 18 : 15} />
              {!isCollapsed && (
                <span>
                  <b>Update available</b>
                  <i>Log out and back in to load it</i>
                </span>
              )}
            </button>
          )}

          {/* Divider */}
          <div className="sidebar-divider" />

          {/* User profile */}
          <div className="user-profile">
            <div className={`user-avatar-wrap${chatUnread > 0 ? ' has-chat-unread' : ''}`}>
              {/* Unread Chat messages make the avatar itself flash, with the count on its free
                  corner (top-right is the ClickHouse light, bottom-right is sign out); it opens Chat. */}
              {chatUnread > 0 ? (
                <Link to="/chat" className="user-avatar chat-alert" title={`${chatUnread} unread chat message${chatUnread === 1 ? '' : 's'} — open Chat`}>
                  {initials}
                  <span className="chat-alert-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
                      <path d="M12 3C6.5 3 2 6.9 2 11.7c0 2.4 1.1 4.6 3 6.2L4.2 21.5l4.3-1.9c1.1.3 2.3.5 3.5.5 5.5 0 10-3.9 10-8.7S17.5 3 12 3z" />
                    </svg>
                  </span>
                </Link>
              ) : (
                <div className="user-avatar">{initials}</div>
              )}
              <ClickHouseStatus />
              <button
                type="button"
                className="signout-badge"
                onClick={logout}
                title="Sign out"
                aria-label="Sign out"
              >
                <SignOutIcon />
              </button>
            </div>
            <div className="user-info">
              <div className="user-name">{displayName}</div>
              {chatUnread > 0 ? (
                <Link to="/chat" className="user-role chat-alert-line">
                  {chatUnread > 99 ? '99+' : chatUnread} unread message{chatUnread === 1 ? '' : 's'}
                </Link>
              ) : (
                <div className="user-role">{roleLabel}</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Sidebar;
